/**
 * Responsive Viewer — preview a site across many device frames at once
 *
 * A single WAR entry exposes `device-list/*`, `*.css` and `sandbox.html` to <all_urls>
 * with no use_dynamic_url, so each is fetchable from any page at a fixed URL. The device
 * frame images live under the vendor-specific `device-list/` tree and are WebP, so they
 * load through probeImage with no CORS grant and survive a strict page CSP.
 *
 * Manifest verified against version 1.1.24.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'inmopeiepgfljkpkidclfgbgbmfcennb';

export const responsiveViewer: ExtensionDefinition = {
  id: ID,
  name: 'Responsive Viewer',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 400_000,
  notes:
    'device-list/*, *.css and sandbox.html exposed to <all_urls> with no dynamic URL. A ' +
    'device-frame image loads without a CORS grant and is conclusive.',
  signals: [
    {
      id: 'war-iphone15-frame',
      kind: 'web-accessible-resource',
      description:
        'Load device-list/apple-iphone-15/frame.webp via <img> — a bundled device frame ' +
        'under the vendor-specific device-list tree; needs no CORS grant, so it works even ' +
        'under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'device-list/apple-iphone-15/frame.webp'),
    },
    {
      id: 'war-galaxy-note20-frame',
      kind: 'web-accessible-resource',
      description:
        'Load device-list/galaxy-note20/frame.webp via <img> — a second device frame, ' +
        'corroborating the identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'device-list/galaxy-note20/frame.webp'),
    },
    {
      id: 'war-sandbox-html',
      kind: 'web-accessible-resource',
      description: 'Fetch sandbox.html — the viewer sandbox page, independent of the images.',
      weight: 0.5,
      run: () => probeFetch(ID, 'sandbox.html'),
    },
  ],
};
